import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@common/components/button';
import { Row, Group } from '@common/components/form';
import { TextareaInputHookForm } from '@common/components/form/inputs/textarea/textarea';
import ModalContent from '@common/components/modal/modal-content';
import ModalWindow from '@common/components/modal/modal-window';
import Stack from '@common/components/stack';
import { FileInputHookForm } from '@common/components/form/inputs/file';
import { Icon } from '@common/components/icon';
import { ENotesVisibility, NotesFormData } from '@modules/core/types';
import { Attachment } from '@common/types/objects';
import { AttachmentsHookForm } from '@common/components/form/attachments';
import { SelectInputHookForm } from '@common/components/form/inputs/select';


interface AddNoteFormProps {
  isVisible: boolean;
  onSave: (data: NotesFormData) => void;
  handleClose: () => void;
}

export const AddNoteForm = React.memo(({
  isVisible,
  onSave,
  handleClose
}: AddNoteFormProps) => {
  const { t } = useTranslation();

  const visibilityOptions: any[] = [
    { label: t('core:add_note_visibility_employee'), value: ENotesVisibility.EMPLOYEE },
    { label: t('core:add_note_visibility_all_admins'), value: ENotesVisibility.ALL_ADMINS },
    { label: t('core:add_note_visibility_creator'), value: ENotesVisibility.CREATOR },
  ];

  const {
    control,
    formState: { isSubmitting, isValid },
    handleSubmit,
    register,
  } = useForm<NotesFormData>({
    mode: 'onChange',
    defaultValues: {
      text: '',
      visibility: ENotesVisibility.ALL_ADMINS,
      fileIds: [],
    },
  });

  const attachments = useFieldArray<NotesFormData, 'fileIds', 'id'>({
    name: 'fileIds',
    control,
  });


  const onSubmit = async (data: NotesFormData) => {
    const formattedData: NotesFormData = {
      ...data,
      fileIds: data.fileIds.map((file) => (file as Attachment).id),
    };

    onSave(formattedData);
  };

  return (
    <ModalWindow
      show={isVisible}
      onHide={handleClose}
      size="small"
      list
      className="AddNotes__Modal"
    >
      <ModalContent
        title={t('core:add_note_modal_title')}
        onHide={handleClose}
        customFooter={(
          <Stack direction="row" gap={2} justify="end">
            <Button onClick={handleClose}>
              {t('common:carousel_footer_cancel')}
            </Button>
            <Button type="primary" onClick={handleSubmit(onSubmit)} disabled={!isValid} isLoading={isSubmitting}>
              {t('core:add_note')}
            </Button>
          </Stack>
        )}
      >
        <form onSubmit={handleSubmit(onSubmit)}>

          <Row className="AddNotes__AttachFileRow">
            <TextareaInputHookForm
              className="AddNotes__Textarea"
              control={control}
              minRows={8}
              placeholder={t('core:add_note_description_placeholder')}
              {...register('text', { required: true })}
            />

            <FileInputHookForm
              multiple={3}
              fields={attachments.fields as Attachment[]}
              insert={(index, data) => attachments.insert(index, data as Attachment)}
              update={(index, data) => attachments.update(index, data as Attachment)}
            >
              <Button className="AddNotes__AttachFileButton">
                <Icon type="document" /> {t('core:add_note_attach_file')}
              </Button>
            </FileInputHookForm>
          </Row>
          {attachments.fields.length > 0 &&
            <Row className="AddNotes__AttachmentsRow">
              <AttachmentsHookForm
                {...attachments}
              />

            </Row>}
          <Row>
            <Group>
              <div className="AddNotes__VisibilityRow">
                <label className="AddNotes__VisibilityLabel">
                  <Icon type="visibility" className="tw-mr-1" />{t('core:add_note_visibility_label')}
                </label>
                <SelectInputHookForm
                  className="AddNotes__VisibilitySelect"
                  name="visibility"
                  control={control}
                  options={visibilityOptions}
                  clearable={false}
                />
              </div>
            </Group>
          </Row>
        </form>
      </ModalContent>
    </ModalWindow>
  );
});

export default AddNoteForm;
